import React, { useEffect, useState } from 'react';
import ScheduleBlock from './ScheduleBlock';
import useScheduleStore from '../../stores/scheduleStore';
import useAuthStore from '../../stores/authStore';
import { formatDateToRussian } from '../../utils/dateUtils';
import './Schedule.scss';

// Функция для получения списка месяцев
const getMonthsList = () => {
  const months = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // Добавляем текущий месяц и следующие 3 месяца
  for (let i = 0; i < 4; i++) {
    const month = new Date(currentYear, now.getMonth() + i, 1);
    const monthNumber = month.getMonth() + 1;
    const monthName = month.toLocaleDateString('ru-RU', { month: 'long' });
    months.push({
      value: `${month.getFullYear()}-${String(monthNumber).padStart(2, '0')}`,
      label: `${monthName} ${month.getFullYear()}`,
      year: month.getFullYear(),
      month: monthNumber
    });
  }
  
  return months;
};

// Функция для получения недель в месяце
const getWeeksInMonth = (year, month) => {
  const weeks = [];
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  
  const startDate = new Date(firstDay);
  const dayOfWeek = startDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(startDate.getDate() - daysToSubtract);
  
  let currentWeekStart = new Date(startDate);
  
  while (currentWeekStart <= lastDay) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);
    
    const weekDates = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      return date;
    });
    
    weeks.push({
      start: new Date(currentWeekStart),
      end: new Date(weekEnd),
      dates: weekDates
    });
    
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  return weeks;
};

function Schedule() {
  const { user } = useAuthStore();
  const { fetchActualStudentSchedule } = useScheduleStore();
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [monthlySchedule, setMonthlySchedule] = useState([]);
  const [months] = useState(getMonthsList());

  const days = ['Понедельник','Вторник','Среда','Четверг','Пятница','Суббота','Воскресенье'];
  const displayDays = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

  // Получаем недели выбранного месяца
  const selectedMonthData = months.find(m => m.value === selectedMonth);
  const weeksInMonth = selectedMonthData ? getWeeksInMonth(selectedMonthData.year, selectedMonthData.month) : [];

  useEffect(() => {
    console.log('[Home Schedule] useEffect сработал для месяца:', selectedMonth, 'данные месяца:', selectedMonthData);
    if (!selectedMonthData || !user) return;
    
    // Загружаем расписание для всех недель месяца
    const loadMonthlySchedule = async () => {
      const monthData = [];
      
      for (let weekIdx = 0; weekIdx < weeksInMonth.length; weekIdx++) {
        const weekData = Array.from({ length: 7 }, () => []);
        
        const weekStart = weeksInMonth[weekIdx].start;
        
        try {
          // Вычисляем startDate и endDate для недели
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          const startDateStr = weekStart.toISOString().slice(0, 10);
          const endDateStr = weekEnd.toISOString().slice(0, 10);
          
          // Загружаем расписание для студента (все групповые + индивидуальные)
          let scheduleData = [];
          if (user.studentId) {
            scheduleData = await fetchActualStudentSchedule({ 
              studentId: user.studentId,
              startDate: startDateStr, 
              endDate: endDateStr 
            });
          }
          
          // Обрабатываем полученные данные
          (scheduleData || []).forEach(day => {
            const dayIdx = days.indexOf(day.dayOfWeek);
            if (dayIdx !== -1) {
              // Проверяем, что дата соответствует конкретному дню недели
              const weekDayDate = new Date(weekStart);
              weekDayDate.setDate(weekStart.getDate() + dayIdx);
              const expectedDate = weekDayDate.toISOString().slice(0, 10);
              
              if (day.date === expectedDate || !day.date) {
                day.timeSlots.forEach(slot => {
                  weekData[dayIdx].push(slot);
                });
              }
            }
          });
          
        } catch (error) {
          console.error('[Home Schedule] Ошибка загрузки недели:', error);
        }
        
        monthData.push(weekData);
      }
      
      setMonthlySchedule(monthData);
    };
    
    loadMonthlySchedule();
  }, [selectedMonth, user]);

  return (
    <div className='Schedule'>
      <div className="month-selector">
        <span>Месяц:</span>
        <select 
          value={selectedMonth} 
          onChange={e => setSelectedMonth(e.target.value)}
        >
          {months.map((month) => (
            <option value={month.value} key={month.value}>{month.label}</option>
          ))}
        </select>
      </div>
      
      <div className='Schedule-monthly'>
        {weeksInMonth.map((week, weekIdx) => (
          <div key={weekIdx} className='monthly-week'>
            <div className='week-header'>
              Неделя {formatDateToRussian((() => {
                // Добавляем +1 день для корректного отображения (компенсация смещения на бэкенде)
                const adjustedStart = new Date(week.start);
                adjustedStart.setDate(adjustedStart.getDate() + 1);
                return adjustedStart.toISOString().slice(0, 10);
              })())} - {formatDateToRussian((() => {
                // Добавляем +1 день для корректного отображения (компенсация смещения на бэкенде)
                const adjustedEnd = new Date(week.end);
                adjustedEnd.setDate(adjustedEnd.getDate() + 1);
                return adjustedEnd.toISOString().slice(0, 10);
              })())}
            </div>
            <div className='Schedule-kanban'>
              {days.map((day, dayIdx) => {
                const currentDate = week.dates[dayIdx];
                const isCurrentMonth = currentDate.getMonth() === selectedMonthData?.month - 1;
                
                return (
                  <div className='Schedule__column' key={dayIdx} style={{ 
                    opacity: isCurrentMonth ? 1 : 0.3
                  }}>
                    <div className='title'>
                      {displayDays[dayIdx]} {formatDateToRussian((() => {
                        // Добавляем +1 день для корректного отображения (компенсация смещения на бэкенде)
                        const adjustedDate = new Date(currentDate);
                        adjustedDate.setDate(adjustedDate.getDate() + 1);
                        return adjustedDate.toISOString().slice(0, 10);
                      })())}
                    </div>
                    
                    {/* Отображаем только добавленные занятия */}
                    {(() => {
                      const daySlots = monthlySchedule[weekIdx]?.[dayIdx] || [];
                      return daySlots;
                    })().map((slot, slotIdx) => (
                      <ScheduleBlock 
                        key={slotIdx} 
                        {...slot} 
                        week={1} 
                        dayOfWeek={day} 
                        currentUserId={user?.studentId} 
                        studentId={user?.studentId} 
                        date={currentDate.toISOString().slice(0,10)}
                      />
                    ))}
                    
                    {(() => {
                      const daySlots = monthlySchedule[weekIdx]?.[dayIdx] || [];
                      return daySlots.length === 0 && isCurrentMonth;
                    })() && (
                      <div className="empty-day">
                        Нет занятий
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Schedule;